/**
 * A label's width at `fontSize`, roughly: CJK a whole em, Latin digits and
 * letters about 0.6 of one. Rounded up: a label a little too far apart
 * costs nothing, one too close overlaps its neighbour.
 */
export function textWidth(text: string, fontSize: number): number {
  let ems = 0;
  for (const char of text) ems += char.charCodeAt(0) > 0xff ? 1 : 0.62;
  return Math.ceil(ems * fontSize);
}

/** The text cut to `width`, with an ellipsis when it was cut. */
export function fitText(text: string, width: number, fontSize: number) {
  if (textWidth(text, fontSize) <= width) return text;
  let cut = text;
  while (cut.length > 1 && textWidth(`${cut}…`, fontSize) > width)
    cut = cut.slice(0, -1);
  return `${cut}…`;
}

/**
 * Strip the year from ISO dates (`2026-06-14` → `06-14`), so a daily axis
 * fits; anything else passes through.
 */
export function shortCategory(label: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(label) ? label.slice(5) : label;
}

/**
 * Every how-many categories a label is drawn so neighbours never overlap:
 * a level label needs its own width, a turned one its line height.
 */
export function labelStep(
  labels: string[],
  band: number,
  rotate: 0 | 45 | 90,
  fontSize: number,
): number {
  if (band <= 0) return 1;
  const need =
    rotate === 0
      ? Math.max(0, ...labels.map(label => textWidth(label, fontSize))) + 6
      : rotate === 45
      ? fontSize * 1.5
      : fontSize + 3;
  return Math.max(1, Math.ceil(need / band));
}

/**
 * A data label inside a bar `room` wide: in full, else short
 * (`¥12,345.00` → `1.23万`), else none — never past the bar's edges.
 */
export function labelInside(
  full: string | undefined,
  value: number,
  room: number,
  fontSize: number,
  compact: (value: number) => string,
): string | undefined {
  if (!full) return undefined;
  const fits = (text: string) => textWidth(text, fontSize) <= room - 2;
  if (fits(full)) return full;
  const short = compact(value);
  return fits(short) ? short : undefined;
}

/** Room left between two level labels. */
const kLevelGap = 4;

/** How far a 45° label reaches sideways (and down) per unit of length. */
const kDiagonal = Math.SQRT1_2;

export interface CategoryLabelsInput {
  /** As shown: dates already short. */
  labels: string[];
  /** Each category's centre, in svg x. */
  centers: number[];
  band: number;
  /** The svg's width: no label reaches past either side. */
  width: number;
  /** The most a turned label may reach down. */
  depth: number;
  /** Omitted: level while every label fits its slot, else 45°. */
  rotate?: 0 | 45 | 90;
  /** Time buckets read in sequence: thinned out rather than turned. */
  time?: boolean;
  fontSize: number;
}

export interface CategoryLabels {
  rotate: 0 | 45 | 90;
  /** Every how-many categories a label is drawn. */
  step: number;
  /** As drawn: cut to their room with an ellipsis. */
  labels: string[];
}

/**
 * The category axis's labels. A name too long for its slot is cut with an
 * ellipsis, and when names do not fit level they turn 45° — unless the
 * style fixes the angle. Every category keeps its label as long as one or
 * two characters fit (time buckets thin out instead), and none is cut off
 * by the svg's edge: the full name is the tooltip's.
 */
export function categoryLabels(input: CategoryLabelsInput): CategoryLabels {
  const { labels, centers, band, width, fontSize: f } = input;
  const slot = band - kLevelGap;
  const fitLevel = labels.every(label => textWidth(label, f) <= slot);
  const rotate = input.rotate ?? (fitLevel || input.time ? 0 : (45 as const));
  if (rotate === 0) {
    const shortest = textWidth('一…', f) + kLevelGap;
    const step = fitLevel
      ? 1
      : input.time
      ? labelStep(labels, band, 0, f)
      : Math.max(1, Math.ceil(shortest / band));
    return {
      rotate,
      step,
      labels: labels.map((label, i) => {
        const x = centers[i];
        const room = Math.min(band * step - kLevelGap, 2 * x, 2 * (width - x));
        return fitText(label, room, f);
      }),
    };
  }
  const step = labelStep(labels, band, rotate, f);
  return {
    rotate,
    step,
    labels: labels.map((label, i) => {
      if (rotate === 90) return fitText(label, input.depth, f);
      // A 45° label ends at its category and runs down to the left; its
      // glyphs' height leans that way too.
      const reach = (centers[i] - f * 0.5 - 2) / kDiagonal;
      return fitText(label, Math.min(input.depth / kDiagonal, reach), f);
    }),
  };
}
